import {
  AboveValueConfigKind,
  ActivePeriodsValueConfig,
  AvgSessionLengthValueConfig,
  BelowValueConfigKind,
  BetweenValueConfigKind,
  CohortFilterGranularity,
  ExactlyValueConfigKind,
  InTheLastValueConfigKind,
  UserConversationLengthValueConfig,
  UserConversationsCountValueConfig,
  UserInteractionsValueConfig,
  UserInteractionsValueConfigInteractionsCount,
} from '@/api/generated/model';
import {
  AVG_SESSION_DURATION_UNIT_OPTIONS,
  convertDurationUnitToSeconds,
  convertSecondsToDurationUnit,
  inferDurationUnit,
  type AvgSessionDurationUnit,
} from '@/domains/live-activity/cohorts/components/rule-builder/avg-session-length-duration';
import { IFilterWhenFields, whenFieldsToConfig } from '@/components/filters/filter-when-forms';
import { FILTERS } from '@/models/common/filters';
import { formatDateConfigs, formatValueConfigs } from '@/utils/filter-utils';
import { translate } from '@/utils/translations';
import {
  DEFAULT_SPECIAL_TIME_FIELDS,
  parseSpecialTimeFilterValues,
} from '@/comps/FilterPicker/filter-content-v2/specialTimeFilterAdapter';

export type ValueComparatorKind =
  | AboveValueConfigKind
  | BelowValueConfigKind
  | ExactlyValueConfigKind
  | BetweenValueConfigKind;

export const VALUE_COMPARATOR_KINDS: ValueComparatorKind[] = [
  AboveValueConfigKind.above,
  BelowValueConfigKind.below,
  ExactlyValueConfigKind.exactly,
  BetweenValueConfigKind.between,
];

export type UsersInteractionsFilterState = {
  column: typeof FILTERS.usersInteraction;
  comparatorKind: ValueComparatorKind;
  firstNumber: number;
  secondNumber: number;
  per: CohortFilterGranularity | null;
  whenFields: IFilterWhenFields;
  preservedOffset?: number;
};

export type UserConversationsCountFilterState = {
  column: typeof FILTERS.userConversationsCount;
  comparatorKind: ValueComparatorKind;
  firstNumber: number;
  secondNumber: number;
  preservedOffset?: number;
};

export type UserConversationLengthFilterState = {
  column: typeof FILTERS.userConversationLength;
  comparatorKind: ValueComparatorKind;
  firstNumber: number;
  secondNumber: number;
  preservedOffset?: number;
};

export type ActivePeriodsFilterState = {
  column: typeof FILTERS.activePeriods;
  comparatorKind: ValueComparatorKind;
  firstNumber: number;
  secondNumber: number;
  window: number;
  granularity: CohortFilterGranularity;
};

export type AvgSessionLengthFilterState = {
  column: typeof FILTERS.avgSessionLength;
  comparatorKind: ValueComparatorKind;
  firstNumber: number;
  secondNumber: number;
  durationUnit: AvgSessionDurationUnit;
  whenFields: IFilterWhenFields;
  hasWindow: boolean;
};

export type InteractionSpecialFilterState =
  | UsersInteractionsFilterState
  | UserConversationsCountFilterState
  | UserConversationLengthFilterState
  | ActivePeriodsFilterState
  | AvgSessionLengthFilterState;

export type InteractionSpecialFilterValues =
  | UserInteractionsValueConfig
  | UserConversationsCountValueConfig
  | UserConversationLengthValueConfig
  | ActivePeriodsValueConfig
  | AvgSessionLengthValueConfig;

const DEFAULT_WHEN_FIELDS: IFilterWhenFields = { ...DEFAULT_SPECIAL_TIME_FIELDS };

const DEFAULT_ROLLING_WHEN_FIELDS: IFilterWhenFields = {
  ...DEFAULT_SPECIAL_TIME_FIELDS,
  kind: InTheLastValueConfigKind.in_the_last,
  number: 12,
  granularity: CohortFilterGranularity.week,
};

export function isInteractionSpecialFilter(column: string | null | undefined): boolean {
  return (
    column === FILTERS.usersInteraction ||
    column === FILTERS.userConversationsCount ||
    column === FILTERS.userConversationLength ||
    column === FILTERS.activePeriods ||
    column === FILTERS.avgSessionLength
  );
}

export function getValueComparatorLabel(kind: ValueComparatorKind): string {
  return translate('value_config_kind', kind);
}

function getPreservedOffset(config: { offset?: number } | undefined): number | undefined {
  return typeof config?.offset === 'number' ? config.offset : undefined;
}

function parseComparatorFields(config: {
  kind?: string;
  value?: number | number[];
  per?: CohortFilterGranularity;
  offset?: number;
}): {
  comparatorKind: ValueComparatorKind;
  firstNumber: number;
  secondNumber: number;
  per: CohortFilterGranularity | null;
  preservedOffset?: number;
} {
  const kind = (config.kind ?? AboveValueConfigKind.above) as ValueComparatorKind;
  const preservedOffset = getPreservedOffset(config);

  if (kind === BetweenValueConfigKind.between && Array.isArray(config.value)) {
    return {
      comparatorKind: kind,
      firstNumber: config.value[0] ?? 10,
      secondNumber: config.value[1] ?? 10,
      per: config.per ?? null,
      preservedOffset,
    };
  }

  if (kind === BelowValueConfigKind.below) {
    return {
      comparatorKind: BelowValueConfigKind.below,
      firstNumber: typeof config.value === 'number' ? config.value : 10,
      secondNumber: 10,
      per: config.per ?? null,
      preservedOffset,
    };
  }

  if (kind === ExactlyValueConfigKind.exactly) {
    return {
      comparatorKind: ExactlyValueConfigKind.exactly,
      firstNumber: typeof config.value === 'number' ? config.value : 10,
      secondNumber: 10,
      per: config.per ?? null,
      preservedOffset,
    };
  }

  return {
    comparatorKind: AboveValueConfigKind.above,
    firstNumber: typeof config.value === 'number' ? config.value : 10,
    secondNumber: 10,
    per: config.per ?? null,
    preservedOffset,
  };
}

export function parseInteractionSpecialFilterValues(column: string, values: unknown): InteractionSpecialFilterState {
  if (column === FILTERS.usersInteraction) {
    const config = (values ?? {}) as UserInteractionsValueConfig;
    const parsed = parseComparatorFields(config.interactions_count ?? { kind: 'above', value: 10 });
    return {
      column: FILTERS.usersInteraction,
      comparatorKind: parsed.comparatorKind,
      firstNumber: parsed.firstNumber,
      secondNumber: parsed.secondNumber,
      per: parsed.per,
      whenFields: config.dates ? parseSpecialTimeFilterValues(config.dates).fields : { ...DEFAULT_WHEN_FIELDS },
      preservedOffset: parsed.preservedOffset,
    };
  }

  if (column === FILTERS.userConversationsCount) {
    const config = (values ?? {}) as UserConversationsCountValueConfig;
    const parsed = parseComparatorFields(config.conversations_count ?? { kind: 'above', value: 10 });
    return {
      column: FILTERS.userConversationsCount,
      comparatorKind: parsed.comparatorKind,
      firstNumber: parsed.firstNumber,
      secondNumber: parsed.secondNumber,
      preservedOffset: parsed.preservedOffset,
    };
  }

  if (column === FILTERS.userConversationLength) {
    const config = (values ?? {}) as UserConversationLengthValueConfig;
    const parsed = parseComparatorFields(config.interactions_count ?? { kind: 'above', value: 5 });
    return {
      column: FILTERS.userConversationLength,
      comparatorKind: parsed.comparatorKind,
      firstNumber: parsed.firstNumber,
      secondNumber: parsed.secondNumber,
      preservedOffset: parsed.preservedOffset,
    };
  }

  if (column === FILTERS.activePeriods) {
    const config = (values ?? {}) as ActivePeriodsValueConfig;
    const parsed = parseComparatorFields(config.active_count ?? { kind: 'above', value: 6 });
    return {
      column: FILTERS.activePeriods,
      comparatorKind: parsed.comparatorKind,
      firstNumber: parsed.firstNumber,
      secondNumber: parsed.secondNumber,
      window: config.window ?? 12,
      granularity: config.granularity ?? CohortFilterGranularity.week,
    };
  }

  if (column === FILTERS.avgSessionLength) {
    const config = (values ?? {}) as AvgSessionLengthValueConfig;
    const parsed = parseComparatorFields(config.duration ?? { kind: 'above', value: 120 });
    const durationUnit = inferDurationUnit(parsed.firstNumber);
    return {
      column: FILTERS.avgSessionLength,
      comparatorKind: parsed.comparatorKind,
      firstNumber: parsed.firstNumber,
      secondNumber: parsed.secondNumber,
      durationUnit,
      hasWindow: !!config.dates,
      whenFields: config.dates
        ? parseSpecialTimeFilterValues(config.dates).fields
        : { ...DEFAULT_ROLLING_WHEN_FIELDS },
    };
  }

  return createDefaultInteractionSpecialState(FILTERS.userConversationsCount);
}

function buildCountConfig(state: InteractionSpecialFilterState): UserInteractionsValueConfigInteractionsCount {
  const offset = 'preservedOffset' in state ? (state.preservedOffset ?? new Date().getTimezoneOffset()) : new Date().getTimezoneOffset();

  if (state.comparatorKind === BetweenValueConfigKind.between) {
    const config = {
      kind: BetweenValueConfigKind.between,
      value: [state.firstNumber, state.secondNumber] as [number, number],
      offset,
    } as UserInteractionsValueConfigInteractionsCount;

    if (state.column === FILTERS.usersInteraction && state.per) {
      return { ...config, per: state.per };
    }
    return config;
  }

  if (state.comparatorKind === BelowValueConfigKind.below) {
    const config = {
      kind: BelowValueConfigKind.below,
      value: state.firstNumber,
      offset,
    } as UserInteractionsValueConfigInteractionsCount;

    if (state.column === FILTERS.usersInteraction && state.per) {
      return { ...config, per: state.per };
    }
    return config;
  }

  if (state.comparatorKind === ExactlyValueConfigKind.exactly) {
    const config = {
      kind: ExactlyValueConfigKind.exactly,
      value: state.firstNumber,
      offset,
    } as UserInteractionsValueConfigInteractionsCount;

    if (state.column === FILTERS.usersInteraction && state.per) {
      return { ...config, per: state.per };
    }
    return config;
  }

  const config = {
    kind: AboveValueConfigKind.above,
    value: state.firstNumber,
    offset,
  } as UserInteractionsValueConfigInteractionsCount;

  if (state.column === FILTERS.usersInteraction && state.per) {
    return { ...config, per: state.per };
  }

  return config;
}

export function serializeInteractionSpecialFilterState(
  state: InteractionSpecialFilterState,
): InteractionSpecialFilterValues | null {
  if (state.column === FILTERS.usersInteraction) {
    const whenConfig = whenFieldsToConfig(state.whenFields);
    if (!whenConfig) return null;

    return {
      interactions_count: buildCountConfig(state),
      dates: whenConfig as UserInteractionsValueConfig['dates'],
    };
  }

  if (state.column === FILTERS.userConversationsCount) {
    return {
      conversations_count: buildCountConfig(state) as UserConversationsCountValueConfig['conversations_count'],
    };
  }

  if (state.column === FILTERS.userConversationLength) {
    return {
      interactions_count: buildCountConfig(state) as UserConversationLengthValueConfig['interactions_count'],
    };
  }

  if (state.column === FILTERS.activePeriods) {
    return {
      active_count: buildCountConfig(state) as ActivePeriodsValueConfig['active_count'],
      window: state.window,
      granularity: state.granularity,
    };
  }

  if (state.column === FILTERS.avgSessionLength) {
    const result: AvgSessionLengthValueConfig = {
      duration: buildCountConfig(state) as AvgSessionLengthValueConfig['duration'],
    };

    if (state.hasWindow) {
      const whenConfig = whenFieldsToConfig(state.whenFields);
      if (whenConfig) {
        result.dates = whenConfig as AvgSessionLengthValueConfig['dates'];
      }
    }

    return result;
  }

  return null;
}

export function serializeInteractionSpecialFilterStateForComparatorSelection(
  state: InteractionSpecialFilterState,
): InteractionSpecialFilterValues {
  const fullConfig = serializeInteractionSpecialFilterState(state);
  if (fullConfig) return fullConfig;

  if (state.column === FILTERS.usersInteraction) {
    const whenConfig =
      whenFieldsToConfig(state.whenFields) ??
      ({
        kind: InTheLastValueConfigKind.in_the_last,
        value: state.whenFields.number || 1,
        granularity: state.whenFields.granularity ?? CohortFilterGranularity.day,
      } as UserInteractionsValueConfig['dates']);

    return {
      interactions_count: buildCountConfig(state),
      dates: whenConfig as UserInteractionsValueConfig['dates'],
    };
  }

  if (state.column === FILTERS.userConversationLength) {
    return {
      interactions_count: buildCountConfig(state) as UserConversationLengthValueConfig['interactions_count'],
    };
  }

  if (state.column === FILTERS.activePeriods) {
    return {
      active_count: buildCountConfig(state) as ActivePeriodsValueConfig['active_count'],
      window: state.window,
      granularity: state.granularity,
    };
  }

  if (state.column === FILTERS.avgSessionLength) {
    return {
      duration: buildCountConfig(state) as AvgSessionLengthValueConfig['duration'],
    };
  }

  return {
    conversations_count: buildCountConfig(state) as UserConversationsCountValueConfig['conversations_count'],
  };
}

export function createDefaultInteractionSpecialFilterValues(column: string): InteractionSpecialFilterValues {
  const state = createDefaultInteractionSpecialState(column);
  return serializeInteractionSpecialFilterStateForComparatorSelection(state);
}

export function createDefaultInteractionSpecialState(column: string): InteractionSpecialFilterState {
  if (column === FILTERS.usersInteraction) {
    return {
      column: FILTERS.usersInteraction,
      comparatorKind: AboveValueConfigKind.above,
      firstNumber: 10,
      secondNumber: 10,
      per: null,
      whenFields: { ...DEFAULT_WHEN_FIELDS },
    };
  }

  if (column === FILTERS.userConversationLength) {
    return {
      column: FILTERS.userConversationLength,
      comparatorKind: AboveValueConfigKind.above,
      firstNumber: 5,
      secondNumber: 10,
    };
  }

  if (column === FILTERS.activePeriods) {
    return {
      column: FILTERS.activePeriods,
      comparatorKind: AboveValueConfigKind.above,
      firstNumber: 6,
      secondNumber: 10,
      window: 12,
      granularity: CohortFilterGranularity.week,
    };
  }

  if (column === FILTERS.avgSessionLength) {
    return {
      column: FILTERS.avgSessionLength,
      comparatorKind: AboveValueConfigKind.above,
      firstNumber: 120,
      secondNumber: 120,
      durationUnit: 'seconds',
      hasWindow: true,
      whenFields: { ...DEFAULT_ROLLING_WHEN_FIELDS },
    };
  }

  return {
    column: FILTERS.userConversationsCount,
    comparatorKind: AboveValueConfigKind.above,
    firstNumber: 10,
    secondNumber: 10,
  };
}

export function getInteractionSpecialComparatorFromValues(column: string, values: unknown): ValueComparatorKind | null {
  const state = parseInteractionSpecialFilterValues(column, values);
  return state.comparatorKind ?? null;
}

export function createInteractionSpecialStateForComparator(
  column: string,
  comparatorKind: ValueComparatorKind,
  previous?: InteractionSpecialFilterState,
): InteractionSpecialFilterState {
  const base = previous ?? createDefaultInteractionSpecialState(column);
  const firstNumber =
    base.firstNumber ||
    (column === FILTERS.userConversationLength ? 5 : column === FILTERS.activePeriods ? 6 : column === FILTERS.avgSessionLength ? 120 : 10);

  if (base.column === FILTERS.usersInteraction) {
    return {
      ...base,
      column: FILTERS.usersInteraction,
      comparatorKind,
      firstNumber,
      secondNumber: base.secondNumber || 10,
    };
  }

  if (base.column === FILTERS.userConversationLength) {
    return {
      ...base,
      column: FILTERS.userConversationLength,
      comparatorKind,
      firstNumber,
      secondNumber: base.secondNumber || 10,
    };
  }

  if (base.column === FILTERS.activePeriods) {
    return {
      ...base,
      column: FILTERS.activePeriods,
      comparatorKind,
      firstNumber,
      secondNumber: base.secondNumber || 10,
    };
  }

  if (base.column === FILTERS.avgSessionLength) {
    return {
      ...base,
      column: FILTERS.avgSessionLength,
      comparatorKind,
      firstNumber,
      secondNumber: base.secondNumber || firstNumber,
    };
  }

  return {
    ...base,
    column: FILTERS.userConversationsCount,
    comparatorKind,
    firstNumber,
    secondNumber: base.secondNumber || 10,
  };
}

export function formatDurationUnitLabel(unit: AvgSessionDurationUnit): string {
  return AVG_SESSION_DURATION_UNIT_OPTIONS.find((option) => option.value === unit)?.label ?? unit;
}

function formatAvgSessionDurationSummary(duration: AvgSessionLengthValueConfig['duration']): string {
  if (!duration) return '';

  const kind = duration.kind ?? AboveValueConfigKind.above;
  const kindLabel = translate('value_config_kind', kind);

  if (kind === BetweenValueConfigKind.between && Array.isArray(duration.value)) {
    const [firstSeconds, secondSeconds] = duration.value;
    const unit = inferDurationUnit(typeof firstSeconds === 'number' ? firstSeconds : 120);
    const displayFirst = convertSecondsToDurationUnit(
      typeof firstSeconds === 'number' ? firstSeconds : 120,
      unit,
    );
    const displaySecond = convertSecondsToDurationUnit(
      typeof secondSeconds === 'number' ? secondSeconds : 120,
      unit,
    );
    return `${kindLabel} ${displayFirst} and ${displaySecond} ${formatDurationUnitLabel(unit)}`;
  }

  const seconds = typeof duration.value === 'number' ? duration.value : 120;
  const unit = inferDurationUnit(seconds);
  const displayValue = convertSecondsToDurationUnit(seconds, unit);
  return `${kindLabel} ${displayValue} ${formatDurationUnitLabel(unit)}`;
}

export function formatInteractionSpecialPillSummary(column: string, values: unknown): string | undefined {
  if (!values || typeof values !== 'object') return undefined;

  if (column === FILTERS.usersInteraction) {
    const config = values as UserInteractionsValueConfig;
    if (!config.interactions_count || !config.dates) return undefined;
    return `${formatValueConfigs(config.interactions_count)} ${formatDateConfigs(config.dates)}`.trim();
  }

  if (column === FILTERS.userConversationsCount) {
    const config = values as UserConversationsCountValueConfig;
    if (!config.conversations_count) return undefined;
    return `${formatValueConfigs(config.conversations_count)} conversations`;
  }

  if (column === FILTERS.userConversationLength) {
    const config = values as UserConversationLengthValueConfig;
    if (!config.interactions_count) return undefined;
    return `${formatValueConfigs(config.interactions_count)} turns`;
  }

  if (column === FILTERS.activePeriods) {
    const config = values as ActivePeriodsValueConfig;
    if (!config.active_count) return undefined;
    const granularity = config.granularity ?? CohortFilterGranularity.week;
    return `${formatValueConfigs(config.active_count)} of the last ${config.window} ${granularity}s`;
  }

  if (column === FILTERS.avgSessionLength) {
    const config = values as AvgSessionLengthValueConfig;
    if (!config.duration) return undefined;
    const durationText = formatAvgSessionDurationSummary(config.duration);
    if (config.dates) {
      return `${durationText} ${formatDateConfigs(config.dates)}`.trim();
    }
    return durationText;
  }

  return undefined;
}

export { convertDurationUnitToSeconds, convertSecondsToDurationUnit, inferDurationUnit };
export type { AvgSessionDurationUnit };
